import {
  SCAN_KIND_LABELS,
  SHIPMENT_STAGE_LABELS,
  trackingSourceName,
  type Shipment,
  type ShipmentHealth,
  type ShipmentScan,
  type ShipmentStage,
  type TrackingCheckOutcome,
  type TrackingRefreshOutcome,
  type TrackingRun,
  type TrackingRunStatus,
} from "@estelle/domain";

/**
 * Plain words for shipment tracking (#527, Part B): what a stage, a scan, an
 * outcome and a time are called on screen.
 *
 * No JSX, so the wording can be held by a script (`check:tracking-fields`) as
 * well as drawn by the screens, and one function says it for every screen that
 * needs it — the order page, the quick view, the partners screen.
 */

/** Shopify's word for a shipment, as the order page and the "out of date" note say it. */
export const SHOPIFY_STATUS_LABEL: Record<
  NonNullable<Shipment["status"]>,
  string
> = {
  pending: "Fulfilled",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  ready_for_pickup: "Ready for pickup",
  attempted_delivery: "Delivery attempted",
  not_delivered: "Not delivered",
  delivered: "Delivered",
  failed: "Failed",
  returned: "Returned",
  cancelled: "Cancelled",
};

/** A health that wants somebody to look: the two a list shows on an order. */
export function needsAttention(
  health: ShipmentHealth | null,
): health is "no_update" | "delayed" {
  return health === "no_update" || health === "delayed";
}

/** How a stage is drawn, by what it means to the person reading. */
export type StageTone =
  /** Not on its way yet. */
  | "waiting"
  /** On its way to the customer. */
  | "moving"
  /** Reached the customer. */
  | "done"
  /** A delivery that did not work. */
  | "failed"
  /** On its way back. */
  | "returning"
  /** Over, and not a delivery. */
  | "closed"
  /** Lost or damaged. */
  | "lost";

export const STAGE_TONE: Record<ShipmentStage, StageTone> = {
  created: "waiting",
  picked_up: "moving",
  in_transit: "moving",
  out_for_delivery: "moving",
  attempt_failed: "failed",
  delivered: "done",
  rto_started: "returning",
  rto_in_transit: "returning",
  reached_origin: "returning",
  rto_out_for_delivery: "returning",
  rto_delivered: "closed",
  cancelled: "closed",
  lost: "lost",
  damaged: "lost",
};

/** "Attempt 2 of delivery", or that there has been none. */
export function attemptsLabel(count: number): string {
  return count > 0 ? `Attempt ${String(count)} of delivery` : "No attempt yet";
}

/* ----------------------------------------------------------------- times */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function plural(n: number, unit: string): string {
  return `${String(n)} ${unit}${n === 1 ? "" : "s"}`;
}

/**
 * "3 hours ago". Coarse on purpose: this is how stale a parcel is, to the hour,
 * and the exact time is one tap away (`ExactTime`). A time a little in the
 * future — a courier's clock ahead of ours — reads as just now; one well ahead,
 * such as the next scheduled check, reads as "in 2 hours".
 */
export function timeAgo(value: string, now: number): string {
  const at = Date.parse(value);
  if (Number.isNaN(at)) return value;
  const age = now - at;
  // A time well ahead is a plan, not skew: the next scheduled check.
  if (age < -5 * MINUTE) {
    const ahead = -age;
    if (ahead < HOUR)
      return `in ${plural(Math.ceil(ahead / MINUTE), "minute")}`;
    if (ahead < DAY) return `in ${plural(Math.round(ahead / HOUR), "hour")}`;
    return `in ${plural(Math.round(ahead / DAY), "day")}`;
  }
  if (age < MINUTE) return "just now";
  if (age < HOUR) return `${plural(Math.floor(age / MINUTE), "minute")} ago`;
  if (age < DAY) return `${plural(Math.floor(age / HOUR), "hour")} ago`;
  if (age < 45 * DAY) return `${plural(Math.floor(age / DAY), "day")} ago`;
  return `${plural(Math.floor(age / (30 * DAY)), "month")} ago`;
}

/** The exact time in India, to the minute: what the relative time stands for. */
export function exactIndiaTime(value: string): string {
  const at = Date.parse(value);
  if (Number.isNaN(at)) return value;
  const text = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(at);
  return `${text} IST`;
}

/** Seconds to a wait a person can read: "45 seconds", "2 minutes". */
export function waitWords(seconds: number): string {
  if (seconds < 90) return plural(Math.max(1, Math.round(seconds)), "second");
  return plural(Math.round(seconds / 60), "minute");
}

/* ----------------------------------------------------------------- scans */

/** What a scan says, in a person's words, and whether the courier's own were different. */
export interface ScanWords {
  /** The headline: the stage the scan means, or the courier's words when it means none. */
  title: string;
  /** The courier's words, when they add something the title does not. */
  asReceived: string | null;
  recognised: boolean;
}

/**
 * A scan for a timeline. A scan the status table knows reads as what it means;
 * the courier's own wording stays available under it, secondary. One it does
 * not know is shown exactly as received, and marked so — never reworded.
 */
export function scanWords(scan: ShipmentScan): ScanWords {
  const received = scan.status.trim();
  const code = scan.code?.trim() ?? "";
  // A DTDC scan can carry a code and no status at all.
  const raw = received !== "" ? received : code !== "" ? `code ${code}` : "";

  if (!scan.recognised || scan.kind === null) {
    return {
      title: raw !== "" ? raw : "No status given",
      asReceived: null,
      recognised: false,
    };
  }
  // "Courier update" says nothing a person can use: the courier's words do.
  const title =
    scan.kind === "info" && raw !== "" ? raw : SCAN_KIND_LABELS[scan.kind];
  return {
    title,
    asReceived:
      raw !== "" && raw.toLowerCase() !== title.toLowerCase() ? raw : null,
    recognised: true,
  };
}

/* ------------------------------------------------------- shopify's drift */

/**
 * The "Shopify out of date" note: what each side says, in one line. Null when
 * the two do not disagree, or when there is nothing of Shopify's to quote.
 */
export function outOfDateNote(
  shipment: Pick<
    Shipment,
    | "shopifyOutOfDate"
    | "stage"
    | "status"
    | "trackingSource"
    | "trackingPartner"
  >,
): string | null {
  if (!shipment.shopifyOutOfDate || shipment.stage === null) return null;
  const who = trackingSourceName(shipment) ?? "The courier partner";
  const shopify =
    shipment.status === null ? null : SHOPIFY_STATUS_LABEL[shipment.status];
  const ours = SHIPMENT_STAGE_LABELS[shipment.stage];
  return shopify === null
    ? `${who} says ${ours}. Shopify has not caught up.`
    : `${who} says ${ours}. Shopify still shows ${shopify}.`;
}

/* ---------------------------------------------------------- the outcomes */

export interface OutcomeMessage {
  /** How to draw it: `ok` for something done, `info` for nothing to do, `problem` for a failure. */
  tone: "ok" | "info" | "problem";
  text: string;
}

const plural1 = (n: number, one: string, many: string) =>
  n === 1 ? one : many;

/** What "Refresh tracking" on an order came to, in words. */
export function refreshMessage(
  outcome: TrackingRefreshOutcome,
): OutcomeMessage {
  switch (outcome.kind) {
    case "refreshed": {
      const updated = outcome.sources.reduce((n, s) => n + s.updated, 0);
      const checked = outcome.sources.reduce((n, s) => n + s.checked, 0);
      const failed = outcome.sources.filter(
        (s) =>
          s.status === "failed" || s.status === "partial" || s.failures > 0,
      );
      const reasons = failed
        .map((s) => s.error)
        .filter((reason): reason is string => reason !== null && reason !== "");

      if (failed.length > 0 && updated === 0) {
        return {
          tone: "problem",
          text:
            reasons.length > 0
              ? `Could not refresh: ${reasons.join(" ")}`
              : "Could not refresh tracking. Try again shortly.",
        };
      }
      if (updated > 0) {
        const tail =
          reasons.length > 0
            ? ` One source had a problem: ${reasons.join(" ")}`
            : "";
        return {
          tone: failed.length > 0 ? "problem" : "ok",
          text: `Updated. ${plural(updated, "shipment")} ${plural1(updated, "has", "have")} new tracking.${tail}`,
        };
      }
      return {
        tone: "info",
        text:
          checked > 0
            ? "Nothing new. The courier has nothing newer than this."
            : "Nothing new.",
      };
    }
    case "just_refreshed":
      return {
        tone: "info",
        text: `Just refreshed. Try again in ${waitWords(outcome.retryAfterSeconds)}.`,
      };
    case "nothing_to_check":
      return {
        tone: "info",
        text: "Nothing to check. No shipment on this order is still on its way.",
      };
    case "not_connected":
      return {
        tone: "problem",
        text: "Nothing is connected to track this shipment. Its courier partner is not connected and Shopify's scan history is not available. An administrator can see why in Settings, Logistics partners.",
      };
    case "not_found":
      return {
        tone: "problem",
        text: "This order could not be found. It may have been deleted.",
      };
    case "unavailable":
      return { tone: "problem", text: outcome.message };
    case "forbidden":
      return {
        tone: "problem",
        text: "You do not have permission to refresh tracking.",
      };
  }
}

/** What "Check now" came to, in words. */
export function checkMessage(
  outcome: TrackingCheckOutcome,
  nameOf: (source: string) => string,
): OutcomeMessage {
  switch (outcome.kind) {
    case "started": {
      const names = outcome.sources.map(nameOf);
      return {
        tone: "ok",
        text: `Check started${names.length > 0 ? ` for ${names.join(" and ")}` : ""}. The result appears under Recent checks in a moment.`,
      };
    }
    case "already_running":
      return {
        tone: "info",
        text: "A check is already running. This joined it instead of starting another.",
      };
    case "nothing_to_check":
      return {
        tone: "info",
        text: `Nothing to check. ${outcome.reason} This is recorded under Recent checks.`,
      };
    case "just_checked":
      return {
        tone: "info",
        text: `Checked a moment ago. Try again in ${waitWords(outcome.retryAfterSeconds)}.`,
      };
    case "not_connected":
      return { tone: "problem", text: outcome.message };
    case "unavailable":
      return { tone: "problem", text: outcome.message };
    case "forbidden":
      return {
        tone: "problem",
        text: "You do not have permission to run a check.",
      };
  }
}

/** What went wrong, or why nothing was checked, whichever words the run carries. */
export function runNote(
  run: Pick<TrackingRun, "error" | "note">,
): string | null {
  return run.error ?? run.note;
}

/** How a run's result is worded: colour is never the only signal. */
export const RUN_STATUS_LABEL: Record<TrackingRunStatus, string> = {
  running: "Running",
  ok: "Done",
  partial: "Partly failed",
  failed: "Failed",
  skipped: "Nothing to check",
};

export const RUN_TRIGGER_LABEL = {
  schedule: "Scheduled",
  manual: "Check now",
  order: "Refresh on an order",
} as const;
